import React, { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import {
  ArrowUpTrayIcon,
  CheckBadgeIcon,
  PlayCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Loader from "../common/Loader";
import Button from "../common/Button";
import Input from "../common/Input";
import Modal from "../common/Modal";
import Table from "../common/Table";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { usePiOtaReleases } from "../../hooks/usePiOtaReleases";
import {
  PiOtaRelease,
  UploadPiOtaReleaseFormData,
  uploadPiOtaReleaseSchema,
} from "../../schemas/piOtaReleaseSchema";

const formatBytes = (n: number): string => {
  if (!n) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(k)), sizes.length - 1);
  return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const PiSoftwareUpdates: React.FC = () => {
  const { canCreate, canEdit, canDelete } = useAuthPermissions("pi_ota");

  const {
    releases,
    isLoading,
    refetch,
    uploadRelease,
    activateRelease,
    deleteRelease,
    isUploading,
    isActivating,
    isDeleting,
  } = usePiOtaReleases();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<UploadPiOtaReleaseFormData>({
    resolver: zodResolver(uploadPiOtaReleaseSchema),
    mode: "onBlur",
  });

  const activeRelease = useMemo(
    () => releases.find((r) => r.isActive) || null,
    [releases]
  );

  const handleOpenUpload = () => {
    reset();
    clearErrors();
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsUploadOpen(true);
  };

  const handleCloseUpload = () => {
    setIsUploadOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadSubmit = async (data: UploadPiOtaReleaseFormData) => {
    const fileList = data.file as FileList;
    const file = fileList?.[0];
    if (!file) return;
    try {
      await uploadRelease({
        input: {
          // version omitted → backend auto-bumps patch from latest release.
          releaseNotes: data.releaseNotes?.trim() || undefined,
          // Always activate on upload → backend publishes the MQTT
          // signal and online devices pick it up within seconds.
          activate: true,
          file,
        },
      });
      handleCloseUpload();
      await refetch();
    } catch {
      // toast handled by hook
    }
  };

  const handleActivate = async (release: PiOtaRelease) => {
    const ok = window.confirm(
      `Activate ${release.version}? Every device will pull this on next reboot or MQTT push.`
    );
    if (!ok) return;
    try {
      await activateRelease(release.id);
      await refetch();
    } catch {
      // toast handled
    }
  };

  const handleDelete = async (release: PiOtaRelease) => {
    if (release.isActive) return;
    const ok = window.confirm(
      `Delete release ${release.version}? The ZIP will be removed from S3.`
    );
    if (!ok) return;
    try {
      await deleteRelease(release.id);
      await refetch();
    } catch {
      // toast handled
    }
  };

  const columns: ColumnDef<PiOtaRelease>[] = useMemo(
    () => [
      {
        accessorKey: "version",
        header: "Version",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">
              {row.original.version}
            </span>
            {row.original.isActive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                <CheckBadgeIcon className="h-3 w-3" /> Active
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "sizeBytes",
        header: "Size",
        cell: ({ row }) => formatBytes(row.original.sizeBytes),
      },
      {
        accessorKey: "sha256",
        header: "SHA-256",
        cell: ({ row }) => (
          <span
            className="font-mono text-xs text-gray-500"
            title={row.original.sha256}
          >
            {row.original.sha256.slice(0, 12)}…
          </span>
        ),
      },
      {
        accessorKey: "releaseNotes",
        header: "Notes",
        cell: ({ row }) => (
          <span className="text-sm text-gray-600 line-clamp-2">
            {row.original.releaseNotes || "—"}
          </span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Uploaded",
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
      },
    ],
    []
  );

  const actions = useMemo(
    () => [
      {
        label: "Activate",
        icon: <CheckBadgeIcon className="h-4 w-4" />,
        onClick: (row: PiOtaRelease) => handleActivate(row),
        variant: "success" as const,
        visible: (row: PiOtaRelease) => canEdit && !row.isActive,
        disabled: () => isActivating,
      },
      {
        label: "Delete",
        icon: <TrashIcon className="h-4 w-4" />,
        onClick: (row: PiOtaRelease) => handleDelete(row),
        variant: "danger" as const,
        visible: (row: PiOtaRelease) => canDelete && !row.isActive,
        disabled: () => isDeleting,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, canDelete, isActivating, isDeleting]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">
            Pi Software Updates
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage OTA releases for the Raspberry Pi fleet. Devices download
            the active release on boot, or immediately when you push it.
          </p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button variant="primary" onClick={handleOpenUpload}>
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Upload release
            </Button>
          )}
        </div>
      </div>

      {activeRelease && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <PlayCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              Active release: {activeRelease.version}
            </p>
            {activeRelease.releaseNotes && (
              <p className="text-sm text-green-700 mt-1 whitespace-pre-wrap">
                {activeRelease.releaseNotes}
              </p>
            )}
          </div>
        </div>
      )}

      <Table
        data={releases}
        columns={columns}
        actions={actions}
        searchable
        pagination
      />

      <Modal
        isOpen={isUploadOpen}
        onClose={handleCloseUpload}
        title="Upload Pi software update"
        size="md"
      >
        <form
          onSubmit={handleSubmit(handleUploadSubmit)}
          className="space-y-4"
        >
          <Input
            label="Release notes (optional)"
            placeholder="What's in this release?"
            {...register("releaseNotes")}
            error={errors.releaseNotes}
          />
          <div>
            <Input
              label="ZIP file *"
              name="file"
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  setValue("file", e.target.files as any, {
                    shouldValidate: false,
                  });
                  clearErrors("file");
                } else {
                  setValue("file", undefined as any, { shouldValidate: false });
                }
              }}
              onBlur={() => {
                /* don't validate on blur — native file dialog fires blur immediately */
              }}
              error={errors.file as any}
              ref={fileInputRef}
            />
            <p className="mt-2 text-xs text-gray-500">
              ZIP must contain the project files at its root (main.py,
              config.py, requirements.txt, …). Devices will verify SHA-256
              before applying. The release is activated on upload — online
              devices receive it within seconds via MQTT.
            </p>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseUpload}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={isUploading}>
              Upload
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PiSoftwareUpdates;
